import { create } from "zustand";
import {
  fetchContentPlans as apiFetchContentPlans,
  fetchContentPlan as apiFetchContentPlan,
  createContentPlan as apiCreateContentPlan,
  regenerateWorkstreams as apiRegenerateWorkstreams,
  patchWorkstream as apiPatchWorkstream,
  patchContentItem as apiPatchContentItem,
  generateContentItem as apiGenerateContentItem,
  generateWorkstream as apiGenerateWorkstream,
  generateBrief as apiGenerateBrief,
  deleteContentPlan as apiDeleteContentPlan,
  deleteWorkstream as apiDeleteWorkstream,
  deleteContentIdea as apiDeleteContentIdea,
  publishWorkstream as apiPublishWorkstream,
} from "./contentPlansApi";
import { notifications } from "@mantine/notifications";
import i18n from "../i18n";

// Polling interval in milliseconds
const POLLING_INTERVAL = 5000;

export const useContentPlansStore = create((set, get) => ({
  contentPlans: [],
  currentPlan: null,
  isLoading: false,
  pollingIntervalId: null,

  fetchContentPlans: async (brandId) => {
    set({ isLoading: true });
    try {
      const plans = await apiFetchContentPlans(brandId);
      set({
        contentPlans: plans,
        isLoading: false,
      });
      return plans;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "contentPlans.notifications.fetch_failed",
          "Failed to fetch content plans"
        ),
        color: "red",
      });
      set({ isLoading: false });
      return [];
    }
  },

  fetchContentPlan: async (planId) => {
    set({ isLoading: true });
    try {
      const plan = await apiFetchContentPlan(planId);
      set({
        currentPlan: plan,
        isLoading: false,
      });

      // Check if we need to start polling (items generating)
      const hasGeneratingItems = plan.workstreams?.some((ws) =>
        ws.contentIdeas?.some((item) => item.status === "generating")
      );
      if (hasGeneratingItems) {
        get().startPolling(planId);
      }

      return plan;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "contentPlans.notifications.fetch_failed",
          "Failed to fetch content plan"
        ),
        color: "red",
      });
      set({ isLoading: false });
      return null;
    }
  },

  // Silent fetch for polling (no loading state)
  refreshCurrentPlan: async () => {
    const { currentPlan } = get();
    if (!currentPlan) return null;

    try {
      const plan = await apiFetchContentPlan(currentPlan.id);

      // Check for newly completed items
      const previousGenerating = currentPlan.workstreams?.flatMap(
        (ws) => ws.contentIdeas?.filter((item) => item.status === "generating") || []
      ) || [];

      const nowComplete = previousGenerating.filter((prevItem) => {
        const newWorkstream = plan.workstreams?.find((ws) =>
          ws.contentIdeas?.some((item) => item.id === prevItem.id)
        );
        const newItem = newWorkstream?.contentIdeas?.find(
          (item) => item.id === prevItem.id
        );
        return newItem && newItem.status !== "generating";
      });

      // Show notification for completed items with link to content
      if (nowComplete.length > 0) {
        const completedItem = plan.workstreams
          ?.flatMap((ws) => ws.contentIdeas || [])
          .find(
            (item) =>
              nowComplete.some((prev) => prev.id === item.id) &&
              item.contentDocumentId
          );

        notifications.show({
          title: i18n.t(
            "contentPlans.notifications.generation_complete",
            "Content generation complete!"
          ),
          message: completedItem?.contentDocumentId
            ? `"${completedItem.title}" is ready for review. Open it from the cluster or find it under Content.`
            : `${nowComplete.length} item(s) are now ready for review.`,
          color: "green",
          autoClose: 10000,
        });
      }

      set({ currentPlan: plan });

      // Check if we should stop polling
      const stillGenerating = plan.workstreams?.some((ws) =>
        ws.contentIdeas?.some((item) => item.status === "generating")
      );
      if (!stillGenerating) {
        get().stopPolling();
      }

      return plan;
    } catch (err) {
      console.error("Failed to refresh plan:", err);
      return null;
    }
  },

  startPolling: (planId) => {
    const { pollingIntervalId } = get();

    // Don't start if already polling
    if (pollingIntervalId) return;

    const intervalId = setInterval(() => {
      get().refreshCurrentPlan();
    }, POLLING_INTERVAL);

    set({ pollingIntervalId: intervalId });
  },

  stopPolling: () => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      set({ pollingIntervalId: null });
    }
  },

  createContentPlan: async (planData) => {
    set({ isLoading: true });
    try {
      const newPlan = await apiCreateContentPlan(planData);
      // Assume the API returns the plan with status "Generating"
      set((state) => ({
        contentPlans: [newPlan, ...state.contentPlans],
        isLoading: false,
      }));
      notifications.show({
        title: i18n.t(
          "contentPlans.notifications.create_success",
          "Proposing clusters"
        ),
        message: i18n.t(
          "contentPlans.notifications.create_message",
          "This may take a few minutes."
        ),
        color: "green",
      });
      return newPlan;
    } catch {
      notifications.show({
        title: i18n.t(
          "contentPlans.notifications.create_failed",
          "Failed to create content plan"
        ),
        color: "red",
      });
      set({ isLoading: false });
      return null;
    }
  },

  regenerateWorkstreams: async (planId) => {
    set({ isLoading: true });
    try {
      const updatedPlan = await apiRegenerateWorkstreams(planId);
      set((state) => ({
        currentPlan: updatedPlan,
        contentPlans: state.contentPlans.map((p) =>
          p.id === planId ? { ...p, ...updatedPlan } : p
        ),
        isLoading: false,
      }));
      notifications.show({
        title: "Clusters proposed again",
        message: "New cluster proposals are ready.",
        color: "green",
      });
      return updatedPlan;
    } catch {
      notifications.show({
        title: "Could not propose clusters again",
        color: "red",
      });
      set({ isLoading: false });
      return null;
    }
  },

  patchWorkstream: async (planId, workstreamId, data) => {
    try {
      const updatedWorkstream = await apiPatchWorkstream(
        planId,
        workstreamId,
        data
      );
      set((state) => {
        if (state.currentPlan && state.currentPlan.id === planId) {
          const updatedWorkstreams = state.currentPlan.workstreams.map((ws) =>
            ws.id === workstreamId ? updatedWorkstream : ws
          );
          return {
            currentPlan: {
              ...state.currentPlan,
              workstreams: updatedWorkstreams,
            },
          };
        }
        return state;
      });
      return updatedWorkstream;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "contentPlans.notifications.patch_failed",
          "Failed to update workstream"
        ),
        color: "red",
      });
      return null;
    }
  },

  patchContentItem: async (planId, workstreamId, contentItemId, data) => {
    try {
      const updatedContentItem = await apiPatchContentItem(
        planId,
        workstreamId,
        contentItemId,
        data
      );
      set((state) => {
        if (state.currentPlan && state.currentPlan.id === planId) {
          const updatedWorkstreams = state.currentPlan.workstreams.map((ws) => {
            if (ws.id === workstreamId) {
              const updatedContentItems = ws.contentIdeas.map((item) =>
                item.id === contentItemId ? updatedContentItem : item
              );
              return {
                ...ws,
                contentIdeas: updatedContentItems,
              };
            }
            return ws;
          });
          return {
            currentPlan: {
              ...state.currentPlan,
              workstreams: updatedWorkstreams,
            },
          };
        }
        return state;
      });
      return updatedContentItem;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "contentPlans.notifications.patch_failed",
          "Failed to update content item"
        ),
        color: "red",
      });
      return null;
    }
  },

  generateBrief: async (planId, workstreamId, contentItemId) => {
    try {
      const updatedIdea = await apiGenerateBrief(planId, workstreamId, contentItemId);
      set((state) => {
        if (state.currentPlan?.id === planId) {
          const updatedWorkstreams = state.currentPlan.workstreams.map((ws) => {
            if (ws.id === workstreamId) {
              const updatedContentItems = ws.contentIdeas.map((item) =>
                item.id === contentItemId ? updatedIdea : item
              );
              return { ...ws, contentIdeas: updatedContentItems };
            }
            return ws;
          });
          return {
            currentPlan: { ...state.currentPlan, workstreams: updatedWorkstreams },
          };
        }
        return state;
      });
      notifications.show({
        title: "Notes saved",
        message: "Optional extra instructions for this article.",
        color: "green",
      });
      return updatedIdea;
    } catch (err) {
      notifications.show({
        title: "Failed to generate brief",
        color: "red",
      });
      return null;
    }
  },

  generateContentItem: async (planId, workstreamId, contentItemId) => {
    try {
      const response = await apiGenerateContentItem(
        planId,
        workstreamId,
        contentItemId
      );

      // Update the content item status to generating (job enqueued, contentDocumentId comes later)
      set((state) => {
        if (state.currentPlan && state.currentPlan.id === planId) {
          const updatedWorkstreams = state.currentPlan.workstreams.map((ws) => {
            if (ws.id === workstreamId) {
              const updatedContentItems = ws.contentIdeas.map((item) => {
                if (item.id === contentItemId) {
                  return {
                    ...item,
                    status: "generating",
                    lastError: null,
                  };
                }
                return item;
              });
              return {
                ...ws,
                contentIdeas: updatedContentItems,
              };
            }
            return ws;
          });
          return {
            currentPlan: {
              ...state.currentPlan,
              workstreams: updatedWorkstreams,
            },
          };
        }
        return state;
      });

      notifications.show({
        title: i18n.t(
          "contentPlans.notifications.generate_started",
          "Content generation started!"
        ),
        message: i18n.t(
          "contentPlans.notifications.generate_message",
          "Your content is being generated and will be ready for review soon."
        ),
        color: "blue",
      });

      // Start polling for updates
      get().startPolling(planId);

      return response;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "contentPlans.notifications.generate_failed",
          "Could not start writing"
        ),
        message: err?.message?.includes("Research this cluster first")
          ? "Research this cluster first. Articles are written from a researched dossier."
          : "We could not start writing this article. Retry.",
        color: "red",
      });
      return null;
    }
  },

  generateWorkstream: async (planId, workstreamId) => {
    try {
      const result = await apiGenerateWorkstream(planId, workstreamId);
      await get().fetchContentPlan(planId);
      get().startPolling(planId);
      notifications.show({
        title:
          result?.enqueued > 0
            ? `Writing ${result.enqueued} article${result.enqueued === 1 ? "" : "s"}`
            : "Nothing new to write",
        message:
          result?.skipped > 0
            ? `${result.skipped} already running.`
            : "From this cluster's research. A brief is not required.",
        color: "blue",
      });
      return result;
    } catch (err) {
      notifications.show({
        title: "Could not start writing",
        message: err?.message?.includes("Research this cluster first")
          ? "Research this cluster first. Articles are written from a researched dossier."
          : "We could not start writing these articles. Retry.",
        color: "red",
      });
      return null;
    }
  },

  deleteContentPlan: async (planId) => {
    try {
      await apiDeleteContentPlan(planId);
      set((state) => ({
        contentPlans: state.contentPlans.filter((p) => p.id !== planId),
        currentPlan: state.currentPlan?.id === planId ? null : state.currentPlan,
      }));
      notifications.show({
        title: "Content plan deleted",
        color: "green",
      });
      return true;
    } catch (err) {
      notifications.show({
        title: "Failed to delete content plan",
        color: "red",
      });
      return false;
    }
  },

  deleteWorkstream: async (planId, workstreamId) => {
    try {
      await apiDeleteWorkstream(planId, workstreamId);
      set((state) => {
        if (state.currentPlan?.id === planId) {
          return {
            currentPlan: {
              ...state.currentPlan,
              workstreams: state.currentPlan.workstreams.filter(
                (ws) => ws.id !== workstreamId
              ),
            },
          };
        }
        return state;
      });
      notifications.show({
        title: "Cluster deleted",
        color: "green",
      });
      return true;
    } catch (err) {
      notifications.show({
        title: "Failed to delete cluster",
        color: "red",
      });
      return false;
    }
  },

  deleteContentIdea: async (planId, workstreamId, contentItemId) => {
    try {
      await apiDeleteContentIdea(planId, workstreamId, contentItemId);
      set((state) => {
        if (state.currentPlan?.id === planId) {
          const updatedWorkstreams = state.currentPlan.workstreams.map((ws) => {
            if (ws.id === workstreamId) {
              return {
                ...ws,
                contentIdeas: ws.contentIdeas.filter(
                  (item) => item.id !== contentItemId
                ),
              };
            }
            return ws;
          });
          return {
            currentPlan: {
              ...state.currentPlan,
              workstreams: updatedWorkstreams,
            },
          };
        }
        return state;
      });
      return true;
    } catch (err) {
      notifications.show({
        title: "Failed to delete content idea",
        color: "red",
      });
      return false;
    }
  },

  publishWorkstream: async (planId, workstreamId) => {
    try {
      const result = await apiPublishWorkstream(planId, workstreamId);
      // Refresh the plan to get updated statuses
      await get().fetchContentPlan(planId);
      notifications.show({
        title: `Published ${result.publishedCount} articles`,
        message: result.skippedCount > 0
          ? `${result.skippedCount} skipped (already published or not yet reviewed).`
          : "All articles in this workstream are now live.",
        color: "green",
        autoClose: 8000,
      });
      return result;
    } catch (err) {
      notifications.show({
        title: "Failed to publish workstream",
        message: err?.message || "An error occurred",
        color: "red",
      });
      return null;
    }
  },

  clearCurrentPlan: () => {
    get().stopPolling();
    set({ currentPlan: null });
  },
}));
